import React, { ReactNode, cloneElement } from "react";
import { Calendar } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

interface EmptyStateProps {
  button?: ReactNode;
  buttonTarget?: () => void;
  copy?: string;
  buttonCopy?: string;
  title?: string;
  icon?: React.ReactNode;
  className?: string;
}

export function EmptyState({
  button,
  buttonTarget,
  copy,
  buttonCopy,
  title,
  icon,
  className,
}: EmptyStateProps) {
  const { t } = useTranslation();

  const defaultCopy = t("table.empty.copy");
  const defaultTitle = t("table.empty.title");
  const defaultButtonCopy = t("table.empty.buttonCopy");

  const defaultIcon = (
    <Calendar className="size-12 text-primary-light" aria-hidden="true" />
  );
  const sizedIcon = icon
    ? cloneElement(icon as React.ReactElement<{ className?: string }>, {
        className: "size-12",
      })
    : defaultIcon;

  return (
    <div
      className={cn(
        "flex h-[320px] flex-col items-center justify-center p-8",
        className,
      )}
      role="region"
      aria-label={t("dataTable.emptyState")}
    >
      <div className="h-12 w-12 text-muted-foreground">{sizedIcon}</div>
      <h2 className="sr-only mt-8">{title || defaultTitle}</h2>
      <p
        className="mt-8 w-[241px] text-center text-wrap text-muted-foreground"
        role="status"
      >
        {copy || defaultCopy}
      </p>
      {button
        ? button
        : buttonTarget && (
            <Button
              onClick={buttonTarget}
              variant="default"
              className="mt-8 h-10 bg-green-700 hover:bg-green-800"
              aria-label={`${buttonCopy || defaultButtonCopy} - ${copy || defaultCopy}`}
            >
              {buttonCopy || defaultButtonCopy}
            </Button>
          )}
    </div>
  );
}
